import { create } from 'zustand';

import { isSameTrack, type PlayerTrack } from '@/music/types';

interface AudioPlayerStore {
  activeTrack: PlayerTrack | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  hasError: boolean;
  play: (track: PlayerTrack) => void;
  pause: () => void;
  seek: (time: number) => void;
  stop: () => void;
}

export const useAudioPlayerStore = create<AudioPlayerStore>((set, get) => ({
  activeTrack: null,
  isPlaying: false,
  currentTime: 0,
  duration: 0,
  hasError: false,
  play: (track) => {
    const audio = getAudioElement();
    const current = get().activeTrack;

    if (!current || !isSameTrack(current, track)) {
      // 같은 url이면 loadedmetadata가 다시 발생하지 않으므로 duration을 리셋하지 않음
      if (!current || current.url !== track.url) {
        audio.src = track.url;
        set({ duration: 0, hasError: false });
      }
      const startTime = track.startTime ?? 0;
      audio.currentTime = startTime;
      set({ activeTrack: track, currentTime: startTime });
    }

    audio.play().catch(() => set({ isPlaying: false, hasError: true }));
  },
  pause: () => {
    audioElement?.pause();
  },
  seek: (time) => {
    const { activeTrack } = get();
    if (!activeTrack || !audioElement) return;
    const min = activeTrack.startTime ?? 0;
    const max = activeTrack.endTime ?? audioElement.duration;
    const clamped = Math.max(min, Math.min(max, time));
    audioElement.currentTime = clamped;
    set({ currentTime: clamped });
  },
  stop: () => {
    if (audioElement) {
      audioElement.pause();
      audioElement.removeAttribute('src');
      audioElement.load();
    }
    set({ activeTrack: null, isPlaying: false, currentTime: 0, duration: 0, hasError: false });
  },
}));

let audioElement: HTMLAudioElement | null = null;

function getAudioElement(): HTMLAudioElement {
  if (audioElement) return audioElement;

  const audio = new Audio();
  const setState = useAudioPlayerStore.setState;

  audio.addEventListener('play', () => setState({ isPlaying: true }));
  audio.addEventListener('pause', () => setState({ isPlaying: false }));
  audio.addEventListener('timeupdate', () => {
    const { activeTrack } = useAudioPlayerStore.getState();
    if (activeTrack?.endTime != null && audio.currentTime >= activeTrack.endTime) {
      audio.pause();
      audio.currentTime = activeTrack.startTime ?? 0;
      setState({ isPlaying: false, currentTime: activeTrack.startTime ?? 0 });
      return;
    }
    setState({ currentTime: audio.currentTime });
  });
  audio.addEventListener('loadedmetadata', () => setState({ duration: audio.duration }));
  audio.addEventListener('ended', () => {
    const { activeTrack } = useAudioPlayerStore.getState();
    const startTime = activeTrack?.startTime ?? 0;
    audio.currentTime = startTime;
    setState({ isPlaying: false, currentTime: startTime });
  });
  audio.addEventListener('error', () => setState({ isPlaying: false, hasError: true }));

  audioElement = audio;
  return audio;
}
